import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, FormControlLabel } from '@nexelem/deprecated-theme';
import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { FilterData } from '@nexelem/filters';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { useStore } from 'zustand';
import { useCustomTranslation } from '../../../../hooks';
import { CustomTFunction, TranslationResult } from '../../../../utils';
import { FormTextInput } from '@nexelem/form-components';
import { GridFiltersProps } from '@nexelem/table';

type FormType = {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
};

type EditGridView = {
  id?: string;
  name: string;
  description: string;
  isDefault: boolean;
};
export type CreateOrEditModalProps = Pick<GridFiltersProps, 'store'> & {
  readonly filters?: FilterData;
  readonly editView?: EditGridView;
};

const getSchema = (
  t: CustomTFunction,
  tWrapper: TranslationResult<'components.filters.createOrEditModal'>,
  reservedNames: string[],
) =>
  z.object({
    id: z.string(),
    name: z
      .string()
      .trim()
      .nonempty(t('forms.validation.messages.required', { name: tWrapper('name.label').toLowerCase() }))
      .refine((name) => !reservedNames.includes(name), tWrapper('name.error')),
    description: z.string(),
    isDefault: z.boolean().default(false),
  });

const CreateOrEditFilterModal = ({
  filters,
  store,
  editView = { name: '', description: '', isDefault: false },
}: CreateOrEditModalProps) => {
  const isEdit = !!editView?.id;
  const { t, tWrapper } = useCustomTranslation('components.filters.createOrEditModal');

  const columns = useStore(store, (state) => state.currentColumnsSettings);
  const reservedNames = useStore(store, (state) =>
    state.settings.filter((_) => _.id !== editView?.id).map((_) => _.name),
  );

  const addGridSettings = useStore(store, (state) => state.addSettings);

  const { control, handleSubmit, reset, register, watch, ...methods } = useForm<FormType>({
    defaultValues: {
      ...editView,
      id: editView?.id ?? uuidv4(),
    },
    resolver: zodResolver(getSchema(t, tWrapper, reservedNames)),
  });

  const onCancelNewFilter = useCallback(() => {
    reset();
    closeCreateOrEditFilterModal();
  }, [reset]);

  const onHandleSubmit = useCallback(
    (filters?: FilterData) => (data: FormType) => {
      addGridSettings({ ...data, columns, filters, id: editView?.id });
      reset();
      closeCreateOrEditFilterModal();
    },
    [addGridSettings, columns, editView, reset],
  );

  return (
    <StaticModal
      component="form"
      maxWidth="lg"
      onClose={closeCreateOrEditFilterModal}
      onSubmit={handleSubmit(onHandleSubmit(filters))}
    >
      <FormProvider
        {...methods}
        control={control}
        handleSubmit={handleSubmit}
        register={register}
        reset={reset}
        watch={watch}
      >
        <StaticModal.Header withCloseButton>{tWrapper(`${isEdit ? 'edit' : 'new'}.header`)}</StaticModal.Header>
        <StaticModal.Content className="flex flex-col gap-6">
          {!isEdit && <span className="text-s18">{tWrapper('new.subtitle')}</span>}
          <FormTextInput
            required
            label={tWrapper('name.label')}
            maxLength={35}
            maxWidth={320}
            name="name"
            placeholder={tWrapper('name.placeholder')}
          />
          <FormTextInput
            label={tWrapper('description.name')}
            maxLength={80}
            name="description"
            placeholder={tWrapper('description.placeholder')}
          />
          <FormControlLabel
            {...register('isDefault')}
            control={<Checkbox disableRipple checked={watch('isDefault')} size="small" />}
            label={<span className="text-s16">{tWrapper('description.default')}</span>}
          />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={onCancelNewFilter}>
            {tWrapper('buttons.cancel')}
          </Button>
          <Button type="submit" variant="contained">
            {tWrapper('buttons.save')}
          </Button>
        </StaticModal.Footer>
      </FormProvider>
    </StaticModal>
  );
};

export const { openCreateOrEditFilterModal, closeCreateOrEditFilterModal } = createModal(CreateOrEditFilterModal, {
  name: 'CreateOrEditFilterModal',
});
